import { faEdit, faTrash } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    BoxProps,
    Chip,
    IconButton,
    Box as MuiBox,
    Button as MuiButton,
    styled,
    Typography,
} from "@mui/material";
import { DataGridPro, GridColDef, GridRowParams } from "@mui/x-data-grid-pro";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useGetCategories } from "src/api/category";
import { LanguageContext } from "src/contexts/language";
import { MapContext } from "src/contexts/map";
import { SettingContext, SettingContextProvider } from "src/contexts/settings";
import CreateCategory from "src/dialogs/CreateCategory";
import DeleteDialog from "src/dialogs/DeleteElement";
import { Enum_Entity } from "src/types";
import { Type_Category } from "src/types/category";
import { getIconFromPicto } from "src/utils/category";

export type Type_Category_Row = {
    id: number;
    title: string;
    nature: string;
    parent: Record<string, any> | null;
    primary_color: string;
    background_color?: string;
    border_width?: number;
    border_style?: string;
    picto: string;
    hierarchy: string[];
};

const DataGrid = styled(DataGridPro)(({ theme }) => ({
    width: "98%",
    border: "none",
    marginTop: theme.spacing(2),
    "& .MuiDataGrid-cell": {
        display: "flex",
        alignItems: "center",
    },
}));

const Button = styled(MuiButton)(({ theme }) => ({
    color: theme.palette.secondary.dark,
    borderColor: theme.palette.secondary.dark,
    "&:hover": {
        color: theme.palette.secondary.dark,
        borderColor: theme.palette.secondary.dark,
    },
}));

const Box = styled(MuiBox)<BoxProps>(({ theme }) => ({
    marginTop: theme.spacing(4),
    marginLeft: theme.spacing(3),
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
}));

const ColorChip = styled(Chip)<{ bgcolor: string }>(({ bgcolor }) => ({
    backgroundColor: bgcolor,
    width: "80%",
}));

const CategoryView = () => {
    return (
        <SettingContextProvider>
            <CategorySettings />
        </SettingContextProvider>
    );
};

const CategorySettings = () => {
    const [openCreateForm, setOpenCreateForm] = useState(false);
    const [openDeleteForm, setOpenDeleteForm] = useState(false);

    const { data, isFetched } = useGetCategories();

    const { t } = useTranslation();

    const { language } = useContext(LanguageContext);

    const { updateCategories } = useContext(MapContext);
    const { updateCategory } = useContext(SettingContext);

    const [categoriesRows, setCategoriesRows] = useState([]);

    const handleEdit = (params: GridRowParams) => {
        updateCategory(params as unknown as Type_Category_Row);
        setOpenCreateForm(true);
    };

    const handleDelete = (params: GridRowParams) => {
        updateCategory(params as unknown as Type_Category_Row);
        setOpenDeleteForm(true);
    };

    const columns: GridColDef[] = [
        { field: "nature", headerName: t("category.nature"), flex: 1 },
        {
            field: "primary_color",
            type: "string",
            flex: 1.5,
            renderCell: (params) =>
                params.value ? (
                    <ColorChip
                        bgcolor={params.value}
                        label=""
                        variant="filled"
                    />
                ) : null,
            renderHeader: () => <strong>{t("category.primary_color")}</strong>,
        },
        {
            field: "background_color",
            type: "string",
            flex: 1.5,
            renderCell: (params) =>
                params.value ? (
                    <ColorChip
                        bgcolor={params.value}
                        label=""
                        variant="filled"
                    />
                ) : null,
            renderHeader: () => (
                <strong>{t("category.background_color")}</strong>
            ),
        },
        {
            field: "picto",
            headerName: t("category.picto"),
            flex: 1,
            renderCell: (params) => (
                <FontAwesomeIcon icon={getIconFromPicto(params.value)} />
            ),
        },
        {
            field: "actions",
            headerName: t("actions"),
            flex: 1,
            renderCell: (params) => (
                <>
                    <IconButton
                        size="small"
                        onClick={() => handleEdit(params.row)}
                    >
                        <FontAwesomeIcon icon={faEdit} />
                    </IconButton>
                    <IconButton
                        size="small"
                        onClick={() => handleDelete(params.row)}
                    >
                        <FontAwesomeIcon icon={faTrash} />
                    </IconButton>
                </>
            ),
        },
    ];

    const getParentCategory = (parent_id: number) =>
        data.find((category: Type_Category) => category.id === parent_id);

    useEffect(() => {
        if (isFetched) {
            updateCategories(data);
            setCategoriesRows(
                data.map(
                    (category: Type_Category): Type_Category_Row => ({
                        id: category.id,
                        title: category.title[language],
                        nature: category.nature,
                        parent: category.parent_id
                            ? getParentCategory(category.parent_id)
                            : {},
                        primary_color: category.style.primary_color,
                        background_color: category.style.background_color,
                        picto: category.picto,
                        border_width: category.style.border_width,
                        border_style: category.style.border_style,
                        hierarchy: category.parent_id
                            ? [
                                  data.find(
                                      (cat: Type_Category) =>
                                          cat.id === category.parent_id,
                                  )?.title[language],
                                  category.title[language],
                              ] // Using titles
                            : [category.title[language]],
                    }),
                ),
            );
        }
    }, [data, isFetched, language]);

    return (
        <MuiBox p={2}>
            <Box>
                <Typography variant="h5">{t("category.manage")}</Typography>
                <Button
                    variant="outlined"
                    onClick={() => {
                        setOpenCreateForm(true);
                        updateCategory(null);
                    }}
                >
                    {t("category.create_category")}
                </Button>
            </Box>
            <DataGrid
                rows={categoriesRows}
                columns={columns}
                treeData
                getTreeDataPath={(row) => row.hierarchy}
            />

            {openCreateForm && (
                <CreateCategory close={() => setOpenCreateForm(false)} />
            )}

            {openDeleteForm && (
                <DeleteDialog
                    closeDeleteDialog={() => setOpenDeleteForm(false)}
                    entity={Enum_Entity.CATEGORY}
                />
            )}
        </MuiBox>
    );
};

export default CategoryView;
